import { useEffect, useRef, useState } from 'react';

/**
 * A price with its change and a flash when it moves -- the trading-app touch.
 * Direction is always carried by ▲/▼ as well as colour. The flash is a CSS
 * animation, so "reduce motion" switches it off.
 */
export function LivePrice({
  value,
  change,
  unit = '',
  size = 'md',
  label,
  formatChange,
}: {
  value: string;
  change: number | null;
  unit?: string;
  size?: 'md' | 'lg';
  label?: string;
  /** How to print the size of the change; the arrow and sign are added either way. */
  formatChange?: (abs: number) => string;
}) {
  const prev = useRef(value);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    if (prev.current === value) return;
    prev.current = value;
    setFlash((change ?? 0) >= 0 ? 'flash-up' : 'flash-down');
    const t = setTimeout(() => setFlash(''), 900);
    return () => clearTimeout(t);
  }, [value, change]);

  const c = change ?? 0;
  const dir = c > 0 ? 'text-up' : c < 0 ? 'text-down' : 'text-faint';
  const arrow = c > 0 ? '▲' : c < 0 ? '▼' : '—';
  const amount = formatChange
    ? formatChange(Math.abs(c))
    : Number.isInteger(Math.abs(c)) ? Math.abs(c).toFixed(0) : Math.abs(c).toFixed(1);
  return (
    <span className={`live-price ${size === 'lg' ? 'lg' : ''} ${flash}`}>
      <span className="live-price-value">{value}</span>
      {change != null && (
        <span className={`live-price-change ${dir}`} aria-label={`${c > 0 ? 'up' : c < 0 ? 'down' : 'unchanged'} ${amount}${unit}${label ? ` ${label}` : ''}`}>
          {arrow} {amount}{unit}{label ? <span className="live-price-label"> {label}</span> : null}
        </span>
      )}
    </span>
  );
}
